import React, { Fragment, useEffect, useState } from "react";
import { clone, equals, lensPath, lensProp } from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FieldOptionNameSelect from "./FieldOptionNameSelect";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import AudienceSelect from "../../creator-application/common/AudienceSelect";
import {
  alwaysRequired,
  defaultValueAllowed,
  fieldOptionAllowed,
  Fields,
} from "../FieldOptions";
import LanguageSelect from "../../creator-application/content/edit/LanguageSelect";
import LicenseTypeSelect from "../../creator-application/content/edit/LicenseTypeSelect";
import VideoRatingsSelect from "./VideoRatingsSelect";
import SubProjectSelect from "./SubProjectSelect";
import CountrySelect from "./CountrySelect";
import RegionSelect from "./RegionSelect";
import CustomDropdownSelect from "./CustomDropdownSelect";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const defaultValueErrorMsg =
  "A field must have a default value if required and hidden";
const hiddenFieldErrorMsg = "A required field cannot be hidden";

const ProjectFieldOptionDialog = ({
  name,
  fieldOption,
  project,
  open,
  onCancel,
  onApply,
}) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    required: false,
    hidden: false,
    fieldLabel: null,
    defaultValue: null,
  });
  const [fieldName, setFieldName] = useState(null);
  const {
    setGeneralError,
    hasGeneralError,
    hasValidationError,
    setValidationErrors,
    resetErrors,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  useEffect(() => {
    if (notBlank(fieldOption)) {
      setFields(clone(fieldOption));
    }

    if (notBlank(name)) {
      setFieldName(name);
    }
  }, [fieldOption, name]);

  const handleTextChange = path => event => {
    const textValue =
      notBlank(event) && notBlank(event.target) ? event.target.value : event;
    const value = notBlank(textValue) ? textValue : null;

    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleFieldNameChange = event => {
    const value = event.target.value;
    setFieldName(value);
    resetErrors();
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleAutocompleteChange = (field, path) => (_event, option) => {
    const value = option[field];
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleApply = async () => {
    if (isBlank(fieldName)) {
      setGeneralError({ message: "A field option name must be selected." });
    } else if (fields.required && fields.hidden) {
      if (defaultValueAllowed(fieldName)) {
        setValidationErrors({
          defaultValue: defaultValueErrorMsg,
        });
      } else {
        setValidationErrors({
          defaultValue: hiddenFieldErrorMsg,
        });
      }
    } else {
      onApply(fieldName, fields);
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">New Field Option</DialogTitle>
      <DialogContent>
        <DialogContentText>Enter field option properties.</DialogContentText>
        <DialogErrors
          showValidationErrors={hasValidationError("defaultValue")}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldOptionNameSelect
              id="field-option-name"
              title="Field Option Name"
              value={fieldName}
              onChange={handleFieldNameChange}
              disabled={notBlank(fieldOption)}
              error={hasGeneralError()}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="field-option-label"
              fullWidth
              label="Field Label"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.fieldLabel || ""}
              onChange={handleTextChange(["fieldLabel"])}
            />
          </Grid>

          {!alwaysRequired(fieldName) && (
            <Fragment>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={fields.hidden}
                      onChange={handleCheckboxChange("hidden")}
                      name="field-option-hidden"
                      inputProps={{ "aria-label": "Select" }}
                    />
                  }
                  label="Field Hidden"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={fields.required}
                      onChange={handleCheckboxChange("required")}
                      name="field-option-required"
                      inputProps={{ "aria-label": "Select" }}
                    />
                  }
                  label="Field Required"
                />
              </Grid>
            </Fragment>
          )}

          {fieldOptionAllowed(fieldName, "defaultValue") && (
            <Grid item xs={12}>
              {equals(fieldName, Fields.audience.value) ? (
                <AudienceSelect
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : equals(fieldName, Fields.language.value) ? (
                <LanguageSelect
                  value={fields.defaultValue}
                  onChange={handleAutocompleteChange("isoCode639_2", [
                    "defaultValue",
                  ])}
                />
              ) : equals(fieldName, Fields.license_type.value) ? (
                <LicenseTypeSelect
                  value={fields.defaultValue}
                  onChange={handleAutocompleteChange("id", ["defaultValue"])}
                />
              ) : equals(fieldName, Fields.rating.value) ? (
                <VideoRatingsSelect
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : equals(fieldName, Fields.country.value) ? (
                <CountrySelect
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : equals(fieldName, Fields.region.value) ? (
                <RegionSelect
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : equals(fieldName, Fields.sub_projects.value) ? (
                <SubProjectSelect
                  subProjects={project.subProjects}
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : equals(fieldName, Fields.custom_dropdown.value) ? (
                <CustomDropdownSelect
                  dropdownItems={project.customDropdownItems}
                  value={fields.defaultValue}
                  onChange={handleTextChange(["defaultValue"])}
                />
              ) : (
                <TextField
                  id="field-option-default-value"
                  fullWidth
                  label="Default Value"
                  InputProps={{
                    classes: { root: classes.title },
                  }}
                  error={hasValidationError("defaultValue")}
                  value={fields.defaultValue || ""}
                  onChange={handleTextChange(["defaultValue"])}
                />
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectFieldOptionDialog;
